import {
  Avatar,
  Box,
  Divider,
  ListItem,
  ListItemIcon,
  Menu,
  MenuItem,
} from "@mui/material";
import { KaerIcon } from "@/icons/kaerIcon";
import CloseIcon from "@mui/icons-material/Close";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import "@/components/styles/headerBar.css";
import styles from "@/components/headerbar/accountSettings.module.css";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";

interface Props {
  handleOpenSettings?: (event: any) => void;
  handleCloseSettings?: (event: any) => void;
  handleNavigateSettings?: () => void;
  combinedSignout: () => void;
  signOut: () => void;
  user?: any;
  email?: any;
  open: boolean;
  firstNameInitial?: string;
  lastNameInitial?: string;
  anchorEl?: any;
}

const AccountSettings: FC<Props> = ({
  handleOpenSettings,
  handleCloseSettings,
  handleNavigateSettings,
  combinedSignout,
  // eslint-disable-next-line
  signOut,
  open,
  firstNameInitial,
  lastNameInitial,
  anchorEl,
  user,
  email,
}) => {
  const { handleError, errorModal } = useErrorModal();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showConfirmationModal } = useConfirmationModal();
  const { t } = useTranslation();
  const MANUAL_LOGOUT_STORAGE_KEY = "manual_logout";
  const MANUAL_LOGOUT_PATH_STORAGE_KEY = "manual_logout_path";
  const RETURN_TO_STORAGE_KEY = "post_login_return_to";

  return (
    <>
      <GenericButton
        className={`headerBarUserIcon ${styles.iconButton}`}
        variant="contained"
        color="tertiary"
        onClick={handleOpenSettings}
        size="medium"
        aria-controls={open ? "userMenu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        data-testid="user-icon"
      >
        {firstNameInitial}
        {lastNameInitial}
      </GenericButton>
      <Menu
        id="userMenu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseSettings}
        slotProps={{
          paper: {
            sx: {
              overflow: "visible",
              filter: isMobile
                ? "drop-shadow(0px 10px 20px rgba(0,0,0,0.9))"
                : "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: isMobile ? 2.0 : 1.5,
              pt: isMobile ? 2 : undefined,
              width: isMobile ? "100%" : undefined,
              "& .MuiAvatar-root": {
                width: 36,
                height: 36,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: isMobile ? 13 : 22,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {isMobile && (
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={handleCloseSettings}
            className={styles.closeButton}
          >
            <CloseIcon />
          </GenericButton>
        )}
        <ListItem className={styles.listItem}>
          <Avatar />
          <Box className={styles.userDetailsWrapper}>
            <Box component={"span"} className="headerBarMenuUserName">
              {user}
            </Box>
            <Box component={"span"} className="headerBarMenuUserMail">
              {email}
            </Box>
          </Box>
        </ListItem>
        <Divider />
        <MenuItem aria-label="settings" onClick={handleNavigateSettings}>
          <ListItemIcon>
            <KaerIcon icon="settings" />
          </ListItemIcon>
          {t("settings")}
        </MenuItem>
        <MenuItem
          aria-label="logout"
          onClick={() => {
            sessionStorage.setItem(MANUAL_LOGOUT_STORAGE_KEY, "true");
            sessionStorage.setItem(
              MANUAL_LOGOUT_PATH_STORAGE_KEY,
              `${window.location.pathname}${window.location.search}${window.location.hash}`
            );
            sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
            api.genericApiRequest({
              entity: "logout",
              method: "post",
              successHandler: () => {
                combinedSignout();
              },
              failHandler: (error: any) => {
                combinedSignout();
                handleError(error);
                showConfirmationModal(t("error_on_logout"));
              },
            });
          }}
          data-testid="logout"
        >
          <ListItemIcon>
            <KaerIcon icon="logout" />
          </ListItemIcon>
          {t("logout")}
        </MenuItem>
      </Menu>
      {errorModal}
    </>
  );
};

export default AccountSettings;

//TODO CUSTOMERROR
